import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Select, SelectItem } from '@heroui/react';
import { Button } from '@/components/ui/Button';
import { useCreateZoneRecord, useUpdateZoneRecord } from '@/features/domains/hooks';
import { dnsRecordSchema, type DnsRecordFormValues } from '@/features/domains/schema';
import type { ZoneRecord, DnsRecordType } from '@/features/domains/types';

const TYPES: DnsRecordType[] = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'CAA'];
const NEEDS_PRIORITY = (t: string) => t === 'MX' || t === 'SRV';

/**
 * Create/edit a zone record in managed-DNS mode. RHF+Zod mirroring the backend
 * DTO limits; on edit, type + host are fixed (only value/priority/TTL change).
 * System-managed records never reach this modal.
 */
export function DnsRecordModal({ isOpen, onClose, domainId, record }: {
  isOpen: boolean;
  onClose: () => void;
  domainId: string;
  record?: ZoneRecord | null;
}) {
  const isEdit = !!record;
  const create = useCreateZoneRecord(domainId);
  const update = useUpdateZoneRecord(domainId);
  const form = useForm<DnsRecordFormValues>({
    resolver: zodResolver(dnsRecordSchema),
    defaultValues: { type: 'A', host: '', value: '', ttl: 3600 },
  });
  const type = form.watch('type');

  useEffect(() => {
    if (record) form.reset({ type: record.type === 'NS' ? 'A' : record.type, host: record.name, value: record.content, priority: record.priority ?? undefined, ttl: record.ttl });
    else form.reset({ type: 'A', host: '', value: '', priority: undefined, ttl: 3600 });
  }, [record, isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = form.handleSubmit(async (v) => {
    const body = { type: v.type, name: v.host.trim(), content: v.value.trim(), priority: NEEDS_PRIORITY(v.type) ? v.priority : undefined, ttl: v.ttl };
    if (isEdit) await update.mutateAsync({ recordId: record!.id, body });
    else await create.mutateAsync(body);
    onClose();
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalContent>
        <ModalHeader className="text-fg">{isEdit ? 'Edit DNS record' : 'Add DNS record'}</ModalHeader>
        <ModalBody>
          <form id="dns-record-form" onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="grid grid-cols-2 gap-3">
              <Controller name="type" control={form.control} render={({ field }) => (
                <Select label="Type" variant="bordered" isDisabled={isEdit} selectedKeys={new Set([field.value])}
                  onSelectionChange={(k) => field.onChange(String(Array.from(k)[0] ?? 'A'))}>
                  {TYPES.map((t) => <SelectItem key={t}>{t}</SelectItem>)}
                </Select>
              )} />
              <Input {...form.register('ttl')} type="number" label="TTL (seconds)" variant="bordered"
                isInvalid={!!form.formState.errors.ttl} errorMessage={form.formState.errors.ttl?.message} />
            </div>
            <Input {...form.register('host')} label="Host / Name" placeholder="@ or www" variant="bordered" isReadOnly={isEdit}
              isInvalid={!!form.formState.errors.host} errorMessage={form.formState.errors.host?.message} />
            <Input {...form.register('value')} label="Value" placeholder={type === 'MX' ? 'mail.acme.com' : type === 'TXT' ? 'v=spf1 ...' : '203.0.113.10'} variant="bordered"
              isInvalid={!!form.formState.errors.value} errorMessage={form.formState.errors.value?.message} />
            {NEEDS_PRIORITY(type) && (
              <Input {...form.register('priority')} type="number" label="Priority" placeholder="10" variant="bordered"
                isInvalid={!!form.formState.errors.priority} errorMessage={form.formState.errors.priority?.message} />
            )}
          </form>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={onClose}>Cancel</Button>
          <Button color="primary" type="submit" form="dns-record-form" isLoading={form.formState.isSubmitting}>
            {isEdit ? 'Save' : 'Add record'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
